import React from 'react';
import { Facebook, Instagram, Youtube, MessageCircle, Calendar, BookOpen, Star } from 'lucide-react';

export const Footer = () => {
  const socialLinks = [
    {
      name: 'Facebook',
      url: 'https://facebook.com/needahairmakeover2',
      icon: Facebook,
      color: 'hover:text-blue-600'
    },
    {
      name: 'Instagram', 
      url: 'https://instagram.com/needahairmakeover',
      icon: Instagram,
      color: 'hover:text-pink-500'
    },
    {
      name: 'YouTube',
      url: 'https://www.youtube.com/@needahairmakeover',
      icon: Youtube,
      color: 'hover:text-red-600'
    },
    {
      name: 'Bluesky',
      url: 'https://bsky.app/profile/needahairmakeover.bsky.social',
      icon: MessageCircle,
      color: 'hover:text-blue-500'
    },
    {
      name: 'Substack',
      url: 'https://substack.com/@ladydianeshawe',
      icon: BookOpen,
      color: 'hover:text-orange-500'
    },
    {
      name: 'Mastodon',
      url: 'https://mastodon.social/@Dianeshawe',
      icon: MessageCircle,
      color: 'hover:text-purple-500'
    },
    {
      name: 'Threads',
      url: 'https://threads.com/needahairmakeover',
      icon: MessageCircle,
      color: 'hover:text-gray-900'
    },
    {
      name: 'TikTok',
      url: 'https://tiktok.com/needahairmakeover',
      icon: MessageCircle,
      color: 'hover:text-black'
    }
  ];

  const additionalLinks = [
    {
      name: 'Book Appointment',
      url: 'https://www.needahairmakeover.com',
      icon: Calendar,
      color: 'hover:text-accent-primary'
    },
    {
      name: 'Follow Our Blog',
      url: 'https://www.needahairmakeover.blog',
      icon: BookOpen,
      color: 'hover:text-accent-primary'
    },
    {
      name: 'Publications',
      url: 'https://www.amazon.co.uk/stores/Diane-Shawe/author/B0052WG8V6',
      icon: BookOpen,
      color: 'hover:text-accent-primary'
    },
    {
      name: 'Reviews',
      url: 'https://www.fresha.com/a/need-a-makeover-hair-dudley-need-a-hair-makeover-208-high-street-wi25twpi#modal-reviews',
      icon: Star,
      color: 'hover:text-accent-primary'
    }
  ];

  return (
    <footer className="bg-dark text-white py-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-8">
          
          {/* Company Info */}
          <div className="md:col-span-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_agi-layer/artifacts/qru87chk_Need%20a%20hair%20makeover%20letterhead%20banner_20250404_081637_0001.png"
              alt="Need A Hair Makeover"
              className="h-16 mb-6 brightness-0 invert"
            />
            <p className="text-gray-300 mb-6 leading-relaxed">
              Award-winning specialist salon franchise offering hair extensions, hair loss treatments, 
              and bespoke hair solutions. Working with celebrities, TV, film, and premium clients across the UK.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-2 text-gray-300">
              <p>📞 <a href="tel:+442030514338" className="hover:text-white transition-colors">+44 20 3051 4338</a></p>
              <p>📧 <a href="mailto:needahairmakeover@gmail.com" className="hover:text-white transition-colors">needahairmakeover@gmail.com</a></p>
              <p>🌐 <a href="https://www.needahairmakeoverfranchise.co.uk" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">www.needahairmakeoverfranchise.co.uk</a></p>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold mb-6 text-white">Quick Links</h3>
            <ul className="space-y-3">
              <li><a href="#about" className="text-gray-300 hover:text-white transition-colors">About Franchise</a></li>
              <li><a href="/hair-extensions-franchise-opportunity" className="text-gray-300 hover:text-white transition-colors">Opportunity</a></li>
              <li><a href="/sponsorship-opportunity" className="text-gray-300 hover:text-white transition-colors">Sponsorship</a></li>
              <li><a href="/resources" className="text-gray-300 hover:text-white transition-colors">Resources</a></li>
              <li><a href="/press" className="text-gray-300 hover:text-white transition-colors">Blog</a></li>
              <li><a href="/training-academy" className="text-gray-300 hover:text-white transition-colors">Training Academy</a></li>
              <li><a href="#apply-form" className="text-gray-300 hover:text-white transition-colors">Apply Now</a></li>
              <li><a href="/admin" className="text-gray-300 hover:text-white transition-colors">Admin Access</a></li>
              <li><a href="/privacy" className="text-gray-300 hover:text-white transition-colors">Privacy Policy</a></li>
            </ul>
            
            {/* Franchise Association Badge */}
            <div className="mt-6 p-4 bg-gray-800 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="bg-accent-primary/20 rounded-full p-2">
                  <span className="text-lg">🏅</span>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Accredited Member</p>
                  <a 
                    href="https://www.franchise-association.org.uk/faouk_directory/need-a-hair-makeover-franchise/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-gray-300 hover:text-white transition-colors"
                  >
                    Franchise Association UK →
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Services & Links */}
          <div>
            <h3 className="text-xl font-bold mb-6 text-white">Our Services</h3>
            <ul className="space-y-3">
              {additionalLinks.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`text-gray-300 transition-colors flex items-center ${link.color}`}
                  >
                    <link.icon className="w-4 h-4 mr-2" />
                    {link.name}
                  </a>
                </li>
              ))}
              <li>
                <a 
                  href="https://www.hairextensionstraining.academy/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Hair Extensions (18 techniques)
                </a>
              </li>
              <li><span className="text-gray-300">Hair Loss Treatments</span></li>
              <li><span className="text-gray-300">Bespoke Handmade Wigs</span></li>
            </ul>
          </div>
        </div>

        {/* Social Media Section */}
        <div className="border-t border-gray-700 mt-12 pt-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold mb-4 text-white">Follow Us on Social Media</h3>
            <p className="text-gray-300 mb-6">Stay connected for the latest updates, success stories, and hair inspiration</p>
          </div>
          
          {/* Social Icons - 4 per row */}
          <div className="flex flex-col items-center gap-4 mb-8">
            <div className="flex justify-center space-x-6">
              {socialLinks.slice(0, 4).map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`p-3 bg-gray-800 rounded-full transition-all duration-300 hover:bg-gray-700 hover:scale-110 ${social.color}`}
                  aria-label={`Follow us on ${social.name}`}
                >
                  <social.icon className="w-6 h-6" />
                </a>
              ))}
            </div>
            <div className="flex justify-center space-x-6">
              {socialLinks.slice(4, 8).map((social, index) => (
                <a
                  key={index + 4}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`p-3 bg-gray-800 rounded-full transition-all duration-300 hover:bg-gray-700 hover:scale-110 ${social.color}`}
                  aria-label={`Follow us on ${social.name}`}
                >
                  <social.icon className="w-6 h-6" />
                </a>
              ))}
            </div>
          </div>

          {/* Social Media Handles - 4 per row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center text-sm text-gray-400 mb-4">
            <div>
              <p className="font-medium text-white">Facebook</p>
              <p>/needahairmakeover2</p>
            </div>
            <div>
              <p className="font-medium text-white">Instagram</p>
              <p>@needahairmakeover</p>
            </div>
            <div>
              <p className="font-medium text-white">YouTube</p>
              <p>@needahairmakeover</p>
            </div>
            <div>
              <p className="font-medium text-white">Bluesky</p>
              <p>@needahairmakeover.bsky.social</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center text-sm text-gray-400">
            <div>
              <p className="font-medium text-white">Substack</p>
              <p>@ladydianeshawe</p>
            </div>
            <div>
              <p className="font-medium text-white">Mastodon</p>
              <p>@Dianeshawe</p>
            </div>
            <div>
              <p className="font-medium text-white">Threads</p>
              <p>@needahairmakeover</p>
            </div>
            <div>
              <p className="font-medium text-white">TikTok</p>
              <p>@needahairmakeover</p>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
          <p>&copy; 2026 Need A Hair Makeover Franchise. All rights reserved.</p>
          <p className="mt-2 text-sm">Specialist salon franchise for hair extensions, hair loss treatments, and bespoke hair solutions.</p>
        </div>
      </div>
    </footer>
  );
};